import { useMemo } from 'react';
import type { SentimentTrendPoint } from '../../types/admin';

interface SentimentTrendChartProps {
  trend: SentimentTrendPoint[];
  className?: string;
}

export default function SentimentTrendChart({ trend, className = '' }: SentimentTrendChartProps) {
  const { points, minScore, maxScore, yRange, width, height } = useMemo(() => {
    if (trend.length === 0) return { points: '', minScore: -1, maxScore: 1, yRange: 1 - (-1), width: 600, height: 200 };

    const scores = trend.map(p => p.score);
    const min = Math.min(...scores, -1);
    const max = Math.max(...scores, 1);
    const w = 600;
    const h = 200;
    const padding = 20;

    // Scale points to SVG coordinates
    const xStep = (w - padding * 2) / (trend.length - 1 || 1);
    const yRange = max - min || 1;

    const scaledPoints = trend.map((point, index) => {
      const x = padding + index * xStep;
      const y = h - padding - ((point.score - min) / yRange) * (h - padding * 2);
      return `${x},${y}`;
    }).join(' ');

    return { points: scaledPoints, minScore: min, maxScore: max, yRange, width: w, height: h };
  }, [trend]);

  if (trend.length === 0) {
    return (
      <div className={`flex items-center justify-center h-48 bg-gray-50 rounded-lg ${className}`}>
        <p className="text-sm text-gray-500">No sentiment data available</p>
      </div>
    );
  }

  const zeroline = height - 20 - ((-minScore) / yRange) * (height - 40);

  return (
    <div className={className}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-48"
        aria-label="Sentiment trend over time"
      >
        {/* Grid lines */}
        <line
          x1="20"
          y1={zeroline}
          x2={width - 20}
          y2={zeroline}
          stroke="#D1D5DB"
          strokeWidth="1"
          strokeDasharray="4 2"
        />

        {/* Zero line label */}
        <text x="5" y={zeroline + 4} fontSize="10" fill="#6B7280">
          0
        </text>

        {/* Positive area background */}
        <rect
          x="20"
          y="20"
          width={width - 40}
          height={zeroline - 20}
          fill="#D1FAE5"
          opacity="0.3"
        />

        {/* Negative area background */}
        <rect
          x="20"
          y={zeroline}
          width={width - 40}
          height={height - 20 - zeroline}
          fill="#FEE2E2"
          opacity="0.3"
        />

        {/* Trend line */}
        <polyline
          points={points}
          fill="none"
          stroke="#4F46E5"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Data points */}
        {trend.map((point, index) => {
          const xStep = (width - 40) / (trend.length - 1 || 1);
          const x = 20 + index * xStep;
          const y = height - 20 - ((point.score - minScore) / yRange) * (height - 40);
          
          const color = point.score < -0.3 ? '#EF4444' : point.score > 0.3 ? '#10B981' : '#6B7280';

          return (
            <g key={index}>
              <circle cx={x} cy={y} r="4" fill={color} stroke="white" strokeWidth="2" />
              <title>
                {new Date(point.timestamp).toLocaleString()}: {point.score.toFixed(2)}
              </title>
            </g>
          );
        })}

        {/* Axis labels */}
        <text x={width / 2} y={height - 5} fontSize="12" fill="#6B7280" textAnchor="middle">
          Time →
        </text>
        <text x="5" y="15" fontSize="10" fill="#10B981">
          +{maxScore.toFixed(1)}
        </text>
        <text x="5" y={height - 10} fontSize="10" fill="#EF4444">
          {minScore.toFixed(1)}
        </text>
      </svg>

      <div className="mt-2 flex items-center justify-center gap-4 text-xs text-gray-600">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          <span>Positive (&gt; 0.3)</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
          <span>Neutral</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
          <span>Negative (&lt; -0.3)</span>
        </div>
      </div>
    </div>
  );
}
